import React, { useMemo } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PlaylistAddCheckOutlinedIcon from "@mui/icons-material/PlaylistAddCheckOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { useTranslation } from "react-i18next";
import { useAgents } from "../../hooks/useAgents";
import { useUsers } from "../../hooks/useUsers";
import AssigneeTransferField from "../AssigneeTransferField";
import LoggedUsersFallbackField from "../LoggedUsersFallbackField";
import {
  handoffTargetToLegacyValues,
  handoffTargetToSelection,
  selectionToHandoffTarget,
} from "../../utils/passagemRecoveryHandoff";
import { selectionRequiresLoggedUsersOnlyDisabled } from "../../utils/assigneeTransferField";
import { applyLoggedUsersOnlyFlag } from "../../utils/loggedUsersFallback";
import {
  movePresellerField,
  normalizePresellerFields,
  PRESSELLER_DEFAULT_MESSAGES,
  presellerFieldHelperKey,
  presellerFieldLabelKey,
} from "../../utils/ticketPresellerConfig";
import { translateSkillForm } from "../../utils/skillLifecycle";

function ConfigAccordion({ title, subtitle, icon: Icon, defaultExpanded = false, children }) {
  return (
    <Accordion
      variant="outlined"
      disableGutters
      defaultExpanded={defaultExpanded}
      sx={{
        "&:before": { display: "none" },
        borderRadius: "12px !important",
        overflow: "hidden",
        borderColor: "divider",
        "&.Mui-expanded": { mb: 0 },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ minHeight: 48, "& .MuiAccordionSummary-content": { my: 1 } }}
      >
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ pr: 1 }}>
          {Icon ? <Icon fontSize="small" color="action" sx={{ mt: 0.35 }} /> : null}
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25, lineHeight: 1.4 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, pb: 2 }}>{children}</AccordionDetails>
    </Accordion>
  );
}

export default function TicketPresellerConfigSection({
  skillConfig = {},
  mergePresellerConfig,
  readOnly = false,
  currentAgentId,
}) {
  const { t } = useTranslation("settings");
  const { data: agentsQuery, isLoading: loadingAgents } = useAgents(1, 100, "");
  const { data: usersData, isLoading: loadingUsers } = useUsers(1, 200, "", "todos");
  const agents = agentsQuery?.agents || [];
  const users = usersData?.users ?? [];

  const handoffAgents = useMemo(
    () =>
      (Array.isArray(agents) ? agents : []).filter(
        (a) =>
          String(a.id) !== String(currentAgentId || "") &&
          (a.status === "active" || a.status === true)
      ),
    [agents, currentAgentId]
  );

  const fields = useMemo(() => normalizePresellerFields(skillConfig), [skillConfig]);
  const handoffLegacyValues = useMemo(
    () => handoffTargetToLegacyValues(skillConfig.handoff_target || {}, { users, agents: handoffAgents }),
    [skillConfig.handoff_target, users, handoffAgents]
  );
  const handoffSelection = useMemo(
    () => handoffTargetToSelection(skillConfig.handoff_target || {}, { users, agents: handoffAgents }),
    [skillConfig.handoff_target, users, handoffAgents]
  );
  const disableLoggedUsersOnly = selectionRequiresLoggedUsersOnlyDisabled(handoffSelection);
  const loggedUsersOnly = skillConfig.handoff_target?.logged_users_only === true;

  const formCopy = (suffix, fallbackKey) => translateSkillForm(t, "ticket_preseller", suffix, fallbackKey);

  const persistFields = (next) => {
    mergePresellerConfig({ collection_fields: next });
  };

  const updateField = (key, partial) => {
    persistFields(fields.map((row) => (row.key === key ? { ...row, ...partial } : row)));
  };

  const moveField = (index, direction) => {
    const toIndex = index + direction;
    persistFields(movePresellerField(fields, index, toIndex));
  };

  const applyHandoffSelection = (selection) => {
    const nextDisable = selectionRequiresLoggedUsersOnlyDisabled(selection);
    mergePresellerConfig({
      handoff_target: selectionToHandoffTarget(selection, {
        logged_users_only: nextDisable
          ? false
          : skillConfig.handoff_target?.logged_users_only === true,
        logged_users_fallback: nextDisable ? null : skillConfig.handoff_target?.logged_users_fallback,
        tipo_id: skillConfig.handoff_target?.tipo_id ?? null,
      }),
    });
  };

  return (
    <Stack spacing={0}>
      <Alert severity="info" sx={{ mb: 1.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.35 }}>
          {t("agentsListPage.presellerIntroTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("agentsListPage.presellerIntroBody")}
        </Typography>
      </Alert>

      <FormControlLabel
        sx={{ mb: 1.5, alignItems: "flex-start", mx: 0 }}
        control={
          <Switch
            checked={skillConfig.offer_whatsapp_flow === true}
            onChange={(e) => mergePresellerConfig({ offer_whatsapp_flow: e.target.checked })}
            disabled={readOnly}
          />
        }
        label={
          <Box>
            <Typography variant="body2">{t("agentsListPage.skillParamsOfferWhatsappFlow")}</Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {t("agentsListPage.skillParamsOfferWhatsappFlowHelper")}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {t("agentsListPage.skillParamsOfferWhatsappFlowGratuitiesHint")}
            </Typography>
          </Box>
        }
      />

      <ConfigAccordion
        icon={PlaylistAddCheckOutlinedIcon}
        title={t("agentsListPage.presellerFieldsTitle")}
        subtitle={t("agentsListPage.presellerFieldsHint")}
        defaultExpanded
      >
        <Stack spacing={1}>
          {fields.map((row, index) => (
            <Box
              key={row.key}
              sx={{
                p: 1.5,
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
                bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "grey.50"),
              }}
            >
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                <Stack direction="row" spacing={0.25} alignItems="center" sx={{ flexShrink: 0 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ minWidth: 22, textAlign: "center", fontWeight: 600 }}
                  >
                    {index + 1}
                  </Typography>
                  <Tooltip title={t("agentsListPage.presellerFieldMoveUp")}>
                    <span>
                      <IconButton
                        size="small"
                        aria-label={t("agentsListPage.presellerFieldMoveUp")}
                        disabled={readOnly || index === 0}
                        onClick={() => moveField(index, -1)}
                      >
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={t("agentsListPage.presellerFieldMoveDown")}>
                    <span>
                      <IconButton
                        size="small"
                        aria-label={t("agentsListPage.presellerFieldMoveDown")}
                        disabled={readOnly || index === fields.length - 1}
                        onClick={() => moveField(index, 1)}
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
                <FormControlLabel
                  sx={{ flex: 1, m: 0 }}
                  control={
                    <Checkbox
                      checked={row.enabled !== false}
                      onChange={(e) => updateField(row.key, { enabled: e.target.checked })}
                      disabled={readOnly}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t(presellerFieldLabelKey(row.key))}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t(presellerFieldHelperKey(row.key))}
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={row.required !== false}
                      onChange={(e) => updateField(row.key, { required: e.target.checked })}
                      disabled={readOnly || row.enabled === false}
                    />
                  }
                  label={t("agentsListPage.presellerFieldRequired")}
                />
              </Stack>
            </Box>
          ))}
        </Stack>
      </ConfigAccordion>

      <ConfigAccordion
        icon={ChatBubbleOutlineOutlinedIcon}
        title={t("agentsListPage.presellerMessagesTitle")}
        subtitle={t("agentsListPage.presellerMessagesHint")}
      >
        <Stack spacing={2}>
          <TextField
            label={t("agentsListPage.presellerIntroMessage")}
            value={skillConfig.intro_message ?? PRESSELLER_DEFAULT_MESSAGES.intro_message}
            onChange={(e) => mergePresellerConfig({ intro_message: e.target.value })}
            disabled={readOnly}
            multiline
            minRows={2}
            fullWidth
          />
          <TextField
            label={t("agentsListPage.presellerTransferMessage")}
            value={skillConfig.transfer_message ?? PRESSELLER_DEFAULT_MESSAGES.transfer_message}
            onChange={(e) => mergePresellerConfig({ transfer_message: e.target.value })}
            disabled={readOnly}
            multiline
            minRows={2}
            fullWidth
          />
        </Stack>
      </ConfigAccordion>

      <ConfigAccordion
        icon={SwapHorizOutlinedIcon}
        title={t("agentsListPage.presellerHandoffTitle")}
        subtitle={formCopy("accordionHandoffSubtitle", "agentsListPage.presellerHandoffHint")}
      >
        <AssigneeTransferField
          label={t("agentsListPage.presellerHandoffTarget")}
          values={handoffLegacyValues}
          users={users}
          agents={handoffAgents}
          loading={loadingUsers || loadingAgents}
          disabled={readOnly}
          helperText={t("agentsListPage.presellerHandoffTargetHelper")}
          onApply={(selection) => applyHandoffSelection(selection)}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={!disableLoggedUsersOnly && loggedUsersOnly}
              disabled={readOnly || disableLoggedUsersOnly}
              onChange={(e) =>
                mergePresellerConfig({
                  handoff_target: applyLoggedUsersOnlyFlag(skillConfig.handoff_target || {}, e.target.checked),
                })
              }
            />
          }
          label={
            <Box>
              <Typography variant="body2">
                {t("agentsListPage.lifecycleHandoffLoggedUsersOnly")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("agentsListPage.lifecycleHandoffLoggedUsersOnlyHelper")}
              </Typography>
            </Box>
          }
        />
        {!disableLoggedUsersOnly && loggedUsersOnly ? (
          <LoggedUsersFallbackField
            fallback={skillConfig.handoff_target?.logged_users_fallback}
            users={users}
            agents={handoffAgents}
            disabled={readOnly}
            onChange={(nextFallback) =>
              mergePresellerConfig({
                handoff_target: {
                  ...(skillConfig.handoff_target || {}),
                  logged_users_only: true,
                  logged_users_fallback: nextFallback,
                },
              })
            }
          />
        ) : null}
      </ConfigAccordion>
    </Stack>
  );
}
